import { Button } from "@chakra-ui/react";
import { Card, ColoredAmount, Link } from "@components/ui-kit";
import { type TColumnDef, useTable } from "@components/ui-kit/table";
import { Trans, t } from "@lingui/macro";
import { TableBody } from "@src/components/modules/common/table";
import { can } from "@src/utils/components/permissions";
import { currency, dateTime } from "@src/utils/formatters";
import { kebabCase } from "lodash";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FC, useMemo } from "react";
import type { ModalReturnCreditProps } from "../ModalReturnCredit/ModalReturnCredit";

interface TData {
  id: string;
  credit: number;
  brandCreditAccount: {
    id: string;
    title: string;
  };
  ourWorkBudgetItem?: {
    id: string;
    title: string;
  } | null;
  expenseBudgetItem?: {
    id: string;
    title: string;
  } | null;
  credit_account_operation_id?: string;
  date: string;
  actions?: {
    onBudgetItem?: (hash: string) => void;
    onOpenReturnCredit?: (props: ModalReturnCreditProps) => void;
  };
}

interface TableCreditAccountsProps {
  data: TData[];
  usedAmount: number;
  onOpenGetCredit?: () => void;
  onBudgetItem?: (hash: string) => void;
  onOpenReturnCredit?: (props: ModalReturnCreditProps) => void;
}

const TableCreditAccounts: FC<
  React.PropsWithChildren<TableCreditAccountsProps>
> = ({
  data,
  usedAmount,
  onBudgetItem,
  onOpenGetCredit,
  onOpenReturnCredit,
}) => {
  const _cols = useMemo(() => {
    let _cols: TColumnDef<TData, any> = cols;
    if (!onOpenReturnCredit) {
      _cols = _cols.filter((i) => i.key !== "actions");
    }

    if (!onBudgetItem) {
      _cols = _cols.filter((i) => i.key !== "budgetItem");
    }

    return _cols;
  }, [onOpenReturnCredit, onBudgetItem]);

  const _data = useMemo(() => {
    return data.map((i) => ({
      ...i,
      actions: { onBudgetItem, onOpenReturnCredit },
    }));
  }, [data, onOpenReturnCredit, onBudgetItem]);

  const { table } = useTable(_data, _cols);

  return (
    <div data-testid="card-list-invoices" className="px-8 py-12">
      <div className="flex items-center h-full mb-10">
        <span className="mr-2">
          <Trans>Used from credit accounts:</Trans>
        </span>
        <span className="font-bold text-2xl">
          {currency.formatByWorkspace(usedAmount)}
        </span>
      </div>

      <Card>
        <div className="pb-10">
          <div className="h-24 flex items-center justify-between px-6">
            <h2 className="text-2xl font-bold">
              <Trans>Credit accounts</Trans>
            </h2>
            <div className="flex">
              {can("credit_transfer_fromBudgetItem") && onOpenGetCredit && (
                <Button
                  mr="3"
                  onClick={onOpenGetCredit}
                >{t`Get credit`}</Button>
              )}
            </div>
          </div>

          <TableBody
            table={table}
            customTBody={
              data.length === 0 ? (
                <tr>
                  <td colSpan={3}>
                    <div className="flex items-center justify-center h-32 w-full">
                      <Trans>Nothing used from credit accounts</Trans>
                    </div>
                  </td>
                </tr>
              ) : undefined
            }
          />
        </div>
      </Card>
    </div>
  );
};

const cols: TColumnDef<TData, any> = [
  {
    key: "date",
    header: () => t`Date`,
    render: (r) => (r.date ? dateTime.format(new Date(r.date)) : ""),
  },
  {
    key: "brandCreditAccount",
    header: () => t`Credit account`,
    render: (r) => (
      <Link
        href={{
          pathname: "/credit-accounts/detail",
          query: { id: r.brandCreditAccount.id },
        }}
      >
        {r.brandCreditAccount.title}
      </Link>
    ),
  },
  {
    key: "budgetItem",
    header: () => t`Budget item`,
    render: (r) => (
      <Button
        onClick={() =>
          r.actions?.onBudgetItem?.(
            kebabCase((r.ourWorkBudgetItem || r.expenseBudgetItem)?.title),
          )
        }
        variant="link"
      >
        {(r.expenseBudgetItem || r.ourWorkBudgetItem)?.title}
      </Button>
    ),
  },
  {
    key: "amount",
    header: () => t`Amount`,
    render: (r) => {
      return (
        <ColoredAmount value={r.credit}>
          {currency.formatByWorkspace(r.credit)}
        </ColoredAmount>
      );
    },
  },
  {
    key: "actions",
    header: () => t`Actions`,
    render: (r) => {
      const onReturnCredit = () => {
        r.actions?.onOpenReturnCredit?.({
          amount: r.credit,
          creditAccount: {
            id: r.brandCreditAccount.id,
            operationId: r.credit_account_operation_id!,
          },
          from: {
            ourWorkBudgetItemId: r.ourWorkBudgetItem?.id,
            expenseBudgetItemId: r.expenseBudgetItem?.id,
          },
        });
      };

      return <Button onClick={onReturnCredit}>{t`Return credit`}</Button>;
    },
  },
];

export default observer(TableCreditAccounts);
